import { CiProductOptionValuesInput } from '__generated__/graphql';
import { Driver, Vehicle, Prices } from 'car-insurance/context';
import { Brand, SortType } from 'car-insurance/types';
import { generateCompareQuoteStaticFields } from 'car-insurance/utils/generateCompareQuoteBody';
import { getDateOfBirth } from 'car-insurance/utils/getDateOfBith';

export const defaultRequestParamsSingSaver = ({
  useBolttechData,
}: {
  useBolttechData: boolean;
}): CiProductOptionValuesInput => ({
  vehicle: {
    brandName: useBolttechData ? 'BT-HOND' : 'HONDA',
    modelCode: useBolttechData ? 'Vezel' : 'MHG.646',
    manufactureYear: 2021,
    ...(useBolttechData && {
      variantCode: 'BT-HOND-0130',
    }),
  },
  mainDriver: {
    dateOfBirth: getDateOfBirth(35),
    age: 35,
    gender: 'M',
    maritalStatus: 'M',
    totalDrivingExperience: useBolttechData ? 5 : 10,
    ...(useBolttechData
      ? {
          occupation: 'indoor',
        }
      : {
          identityType: 'NRIC',
          ...generateCompareQuoteStaticFields(),
        }),
  },
  ncd: 50,
  ...(useBolttechData && {
    offenceFree: 'Y',
    offPeak: 'Y',
    noOfClaims: 0,
  }),
});

export const defaultRequestParamsMoneyHero = {
  vehicle: {
    brandName: 'HONDA',
    modelCode: '1405659',
    manufactureYear: 2022,
  },
  mainDriver: {
    identityType: 'NRIC',
    dateOfBirth: getDateOfBirth(34),
    age: 35,
    gender: 'M',
    maritalStatus: 'M',
    totalDrivingExperience: 3,
    numberOfClaims: 0,
    ...generateCompareQuoteStaticFields(),
  },
  ncd: 60,
};

const defaultDriverSS = ({
  useBolttechData = false,
}: {
  useBolttechData: boolean;
}): Driver => ({
  age: 35,
  dateOfBirth: getDateOfBirth(35),
  gender: 'M',
  maritalStatus: 'M',
  numberOfClaims: 0,
  occupationType: 'indoor',
  totalDrivingExperience: useBolttechData ? 5 : 10,
});

const defaultVehicleSS = ({
  useBolttechData = false,
}: {
  useBolttechData?: boolean;
}): Vehicle => ({
  makeName: useBolttechData ? 'BT-HOND' : 'HONDA',
  modelCode: useBolttechData ? 'Vezel' : 'MHG.646',
  manufactureYear: 2021,
  modelName: 'Vezel 1.5',
  trim: useBolttechData ? 'BT-HOND-0130' : '',
  fairMarket: 0,
  type: '',
});

const defaultDriverMH: Driver = {
  dateOfBirth: getDateOfBirth(35),
  age: 35,
  gender: 'M',
  maritalStatus: 'M',
  totalDrivingExperience: 3,
  numberOfClaims: 0,
};

const defaultVehicleMH: Vehicle = {
  makeName: 'HONDA',
  modelCode: '1405659',
  modelName: 'Jazz',
  manufactureYear: 2022,
  trim: '',
  fairMarket: 0,
  type: '',
};

const defaultDriverMM: Driver = {
  dateOfBirth: getDateOfBirth(35),
  age: 35,
  gender: 'M',
  maritalStatus: 'M',
  totalDrivingExperience: 3,
  numberOfClaims: 0,
};

const defaultVehicleMM: Vehicle = {
  makeName: '',
  modelCode: '',
  modelName: '',
  manufactureYear: '',
  trim: '',
  fairMarket: 0,
  type: 'private',
};

export const defaultContextStateSS = ({
  useBolttechData = false,
}: {
  useBolttechData?: boolean;
}): {
  driver: Driver;
  vehicle: Vehicle;
  ncd: number;
  offenceFree: string;
  offPeakVehicle: string;
} => ({
  driver: defaultDriverSS({ useBolttechData }),
  vehicle: defaultVehicleSS({ useBolttechData }),
  ncd: 50,
  offenceFree: 'Y',
  offPeakVehicle: 'Y',
});

export const defaultContextStateMH = {
  driver: defaultDriverMH,
  vehicle: defaultVehicleMH,
  ncd: 60,
};

export const defaultContextStateMM = {
  driver: defaultDriverMM,
  vehicle: defaultVehicleMM,
  ncd: 30,
  prices: {
    propertyDamage: 200000,
    personalAccident: 350000,
    isActsOfNature: true,
  },
};

export const PROPERTY_DAMAGE_VALUES = [
  50000, 75000, 100000, 150000, 200000, 250000, 300000, 400000, 500000, 750000,
  1000000,
];
export const PERSONAL_ACCIDENT_VALUES = [
  50000, 75000, 100000, 150000, 200000, 250000, 300000, 350000, 400000, 450000,
  500000, 550000, 600000, 650000, 700000, 750000, 800000, 850000, 900000,
  950000, 1000000,
];

export const yearsArray = [
  { value: '2026', label: '2026' },
  { value: '2025', label: '2025' },
  { value: '2024', label: '2024' },
  { value: '2023', label: '2023' },
  { value: '2022', label: '2022' },
  { value: '2021', label: '2021' },
  { value: '2020', label: '2020' },
  { value: '2019', label: '2019' },
  { value: '2018', label: '2018' },
  { value: '2017', label: '2017' },
  { value: '2016', label: '2016' },
  { value: '2015', label: '2015' },
  { value: '2014', label: '2014' },
  { value: '2013', label: '2013' },
  { value: '2012', label: '2012' },
  { value: '2011', label: '2011' },
  { value: '2010', label: '2010' },
  { value: '2009', label: '2009' },
  { value: '2008', label: '2008' },
  { value: '2007', label: '2007' },
  { value: '2006', label: '2006' },
  { value: '2005', label: '2005' },
  { value: '2004', label: '2004' },
];

export const defaultSort: SortType = 'Recommended';

/** Max window for live-premium polling on the car insurance RP, counted from search start (CMS-1898) */
export const CAR_INSURANCE_POLLING_WINDOW_MS = 25000;

export const contactMethodIconOptions = [
  // TODO: do smth with whatsapp icon
  { label: 'whatsapp', value: 'WA' },
  { label: 'phone', value: 'Phone' },
  { label: 'email', value: 'Email' },
];

export const enquiryPurposeOptions = [
  { label: 'How to Apply', value: 'How to Apply' },
  { label: 'Getting a quotation', value: 'Getting a quotation' },
  { label: 'Insurance Product', value: 'Insurance Product' },
  { label: 'Advise', value: 'Advise' },
];

export const monthOptions = [
  { label: 'September', value: '8' },
  { label: 'October', value: '9' },
  { label: 'November', value: '10' },
  { label: 'December', value: '11' },
  { label: 'January', value: '0' },
  { label: 'February', value: '1' },
  { label: 'March', value: '2' },
  { label: 'April', value: '3' },
  { label: 'May', value: '4' },
  { label: 'June', value: '5' },
  { label: 'July', value: '6' },
  { label: 'August', value: '7' },
];

type Option = { label: string; value: string };

export type DefaultContextState = {
  driver: Driver;
  vehicle: Vehicle;
  ncd: number;
  offenceFree?: string;
  offPeakVehicle?: string;
  prices?: Prices;
};

export const config = (
  {
    useBolttechData,
  }: {
    useBolttechData?: boolean;
  } = {
    useBolttechData: false,
  },
): {
  [K in Brand]: {
    ncdOptions: Option[];
    driverAgeOptions: Option[];
    yearsOptions: Option[];
    defaultContextState: DefaultContextState;
  };
} =>
  ({
    singsaver: {
      defaultContextState: defaultContextStateSS({ useBolttechData }),
      ncdOptions: [
        { label: '0%', value: '0' },
        { label: '10%', value: '10' },
        { label: '20%', value: '20' },
        { label: '30%', value: '30' },
        { label: '40%', value: '40' },
        { label: '50%', value: '50' },
      ],
      driverAgeOptions: Array.from(
        { length: 58 },
        (_, index) => index + 18,
      ).map((value) => ({ label: `${value} years`, value: String(value) })),
      yearsOptions: yearsArray,
    },
    moneymax: {
      defaultContextState: defaultContextStateMM,
      ncdOptions: [
        { label: '0%', value: '0' },
        { label: '10%', value: '10' },
        { label: '20%', value: '20' },
        { label: '30%', value: '30' },
        { label: '40%', value: '40' },
        { label: '50%', value: '50' },
      ],
      driverAgeOptions: Array.from(
        { length: 58 },
        (_, index) => index + 18,
      ).map((value) => ({ label: `${value} years`, value: String(value) })),
      yearsOptions: yearsArray,
    },
    moneyhero: {
      defaultContextState: defaultContextStateMH,
      ncdOptions: [
        { label: '0%', value: '0' },
        { label: '10%', value: '10' },
        { label: '20%', value: '20' },
        { label: '30%', value: '30' },
        { label: '40%', value: '40' },
        { label: '50%', value: '50' },
        { label: '60%', value: '60' },
      ],
      driverAgeOptions: Array.from(
        { length: 58 },
        (_, index) => index + 18,
      ).map((value) => ({ label: `${value} 年`, value: String(value) })),
      yearsOptions: yearsArray,
    },
    money101: {
      defaultContextState: defaultContextStateSS({ useBolttechData }),
      ncdOptions: [
        { label: '0%', value: '0' },
        { label: '10%', value: '10' },
        { label: '20%', value: '20' },
        { label: '30%', value: '30' },
        { label: '40%', value: '40' },
        { label: '50%', value: '50' },
      ],
      driverAgeOptions: Array.from(
        { length: 58 },
        (_, index) => index + 18,
      ).map((value) => ({ label: `${value} years`, value: String(value) })),
      yearsOptions: yearsArray,
    },
  }) as {
    [K in Brand]: {
      ncdOptions: Option[];
      driverAgeOptions: Option[];
      yearsOptions: Option[];
      defaultContextState: {
        driver: Driver;
        vehicle: Vehicle;
        ncd: number;
        offenceFree?: string;
        offPeakVehicle?: string;
        prices?: Prices;
      };
    };
  };

export const defaultRequestParamsMoneyMax = {
  // No default vehicle: the user picks one (CMS-2047).
  vehicle: { ...defaultVehicleMM, variantCode: '' },
  driver: {
    identityType: 'NRIC',
    dateOfBirth: defaultDriverMM.dateOfBirth,
    age: defaultDriverMM.age,
    gender: defaultDriverMM.gender,
    maritalStatus: defaultDriverMM.maritalStatus,
    totalDrivingExperience: defaultDriverMM.totalDrivingExperience,
    numberOfClaims: defaultDriverMM.numberOfClaims,
  },
  ncd: defaultContextStateMM.ncd,
};
